import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Job,
  JobApplication,
  JobApplicationStatus,
  User,
} from '@core/generated/gql-types';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import WorkerSearchbar from './workerSearchbar/WorkerSearchbar';
import WorkerApplication from './WorkerApplication';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import AssignmentRateSection from '@/modules/jobs/components/create/AssignmentRateSection';
import {
  EMPTY_ASSIGNMENT_RATE_INPUT,
  getAssignmentRateOverrides,
  type AssignmentRateInput,
  type AssignmentRateInputsByWorkerId,
} from '@/modules/jobs/components/create/assignmentRateInputs';
import useCustomAssignmentRatesAccess from '@/modules/jobs/hooks/useCustomAssignmentRatesAccess';

interface AppliedWorkersProps {
  job: Job;
  jobApplications: JobApplication[];
  openWorkerSearchModal: boolean;
  setLoading: (loading: boolean) => void;
  setOpenWorkerSearchModal: (open: boolean) => void;
}

const AppliedWorkers = ({
  job,
  jobApplications,
  openWorkerSearchModal,
  setLoading,
  setOpenWorkerSearchModal,
}: AppliedWorkersProps) => {
  const { respondToApplication } = useJobApplicationStore();

  const [appliedWorkers, setAppliedWorkers] = useState<User[]>([]);
  const [assignmentRateInputs, setAssignmentRateInputs] =
    useState<AssignmentRateInputsByWorkerId>({});
  const [ratesWorkerId, setRatesWorkerId] = useState<string | null>(null);

  const { workers } = useWorkerStore();
  const fetchDetailedWorkerById = useWorkerStore(
    (state) => state.fetchDetailedWorkerById,
  );
  const hydratedWorkerIds = useRef(new Set<string>());

  const { getTerm } = useTemplateStore();
  const canSetCustomAssignmentRates = useCustomAssignmentRatesAccess(
    job.tenantId,
  );
  const fetchClassificationLevelClientRates = useClassificationStore(
    (state) => state.fetchClassificationLevelClientRates,
  );

  useEffect(() => {
    if (canSetCustomAssignmentRates && job.clientId) {
      void fetchClassificationLevelClientRates(job.clientId);
    }
  }, [
    canSetCustomAssignmentRates,
    fetchClassificationLevelClientRates,
    job.clientId,
  ]);

  const updateAssignmentRateInput = (
    workerId: string,
    updates: Partial<AssignmentRateInput>,
  ) => {
    setAssignmentRateInputs((currentRateInputs) => ({
      ...currentRateInputs,
      [workerId]: {
        ...(currentRateInputs[workerId] ?? EMPTY_ASSIGNMENT_RATE_INPUT),
        ...updates,
      },
    }));
  };

  useEffect(() => {
    // The workers list query omits cvProfile and classificationLevels, so applicants
    // need the detailed record before their summary and rates can render.
    jobApplications.forEach((jobApplication) => {
      if (hydratedWorkerIds.current.has(jobApplication.userId)) {
        return;
      }
      hydratedWorkerIds.current.add(jobApplication.userId);
      void fetchDetailedWorkerById(jobApplication.userId);
    });
  }, [fetchDetailedWorkerById, jobApplications]);

  useEffect(() => {
    // Index workers by id for O(1) lookups while preserving job application order.
    const workerById = new Map(
      workers.map((worker) => [worker.id, worker] as const),
    );

    const orderedAppliedWorkers = jobApplications
      .map((jobApplication) => workerById.get(jobApplication.userId))
      .filter((worker): worker is User => Boolean(worker));

    // De-duplicate workers while keeping the earliest application order.
    const uniqueOrderedAppliedWorkers: User[] = [];
    const seenWorkerIds = new Set<string>();

    for (const worker of orderedAppliedWorkers) {
      if (seenWorkerIds.has(worker.id)) {
        continue;
      }
      seenWorkerIds.add(worker.id);
      uniqueOrderedAppliedWorkers.push(worker);
    }

    setAppliedWorkers(uniqueOrderedAppliedWorkers);
  }, [jobApplications, workers]);

  const handleAssignWorker = useCallback(
    async (worker: User) => {
      try {
        setLoading(true);

        const application = jobApplications.find(
          (app) => app.userId === worker.id,
        );

        if (!application) {
          toast.error('No application found for this worker');
          console.error(`No application found for this worker: ${worker.id}`);
          return;
        }

        const success = await respondToApplication(
          application.id,
          JobApplicationStatus.Accepted,
          getAssignmentRateOverrides(assignmentRateInputs[worker.id]),
        );

        if (!success) {
          toast.error('Error assigning worker to job');
          return;
        }

        toast.success(
          `${worker.firstName} ${worker.lastName} successfully assigned to job!`,
        );
        setRatesWorkerId(null);
      } catch (error) {
        console.error('Error assigning worker to job:', error);
        toast.error(
          error instanceof Error
            ? error.message
            : 'Error assigning worker to job',
        );
      } finally {
        setLoading(false);
        setOpenWorkerSearchModal(false);
      }
    },
    [
      jobApplications,
      assignmentRateInputs,
      respondToApplication,
      setLoading,
      setOpenWorkerSearchModal,
    ],
  );

  if (openWorkerSearchModal) {
    return (
      <WorkerSearchbar
        job={job}
        setLoading={setLoading}
        setOpenWorkerSearchModal={setOpenWorkerSearchModal}
      />
    );
  }

  if (jobApplications.length === 0) {
    return (
      <div className="flex h-12 w-full items-center justify-between gap-2 rounded-md bg-white p-2">
        <div className="flex items-center gap-2">
          <div className="pl-3 text-sm text-gray-600">
            No {getTerm(TemplateTerm.WORKER, false)} applied
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-2">
      {appliedWorkers.map((applyingWorker: User) => {
        const application = jobApplications.find(
          (jobApplication) => jobApplication.userId === applyingWorker.id,
        );

        const isEditingRates = ratesWorkerId === applyingWorker.id;

        return (
          <div key={applyingWorker.id}>
            <WorkerApplication
              worker={applyingWorker}
              job={job}
              coverLetter={application?.coverLetter}
              onAssignWorker={(worker) => {
                if (canSetCustomAssignmentRates && !isEditingRates) {
                  setRatesWorkerId(worker.id);
                  return;
                }
                void handleAssignWorker(worker);
              }}
            />
            {isEditingRates && application && (
              <div>
                <AssignmentRateSection
                  worker={applyingWorker}
                  clientId={job.clientId}
                  classificationId={job.classificationId}
                  rateInput={assignmentRateInputs[applyingWorker.id]}
                  ordinaryWorkerPayRateCents={application.workerRateInCents}
                  ordinaryClientPayRateCents={application.clientRateInCents}
                  onChange={(updates) =>
                    updateAssignmentRateInput(applyingWorker.id, updates)
                  }
                />
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRatesWorkerId(null);
                      updateAssignmentRateInput(
                        applyingWorker.id,
                        EMPTY_ASSIGNMENT_RATE_INPUT,
                      );
                    }}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleAssignWorker(applyingWorker)}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500"
                  >
                    Confirm assignment
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AppliedWorkers;
